import * as t from 'io-ts';

import {findTimeZone} from '../../helpers/dateTimeHelpers';
import {makeEnumRuntimeType} from './apiTypes';
import {
  ChannelComposeModesEnum,
  ChannelContactTypesEnum,
  ChannelReplyModesEnum,
} from './channelBehaviorsModel';
import {ChannelEmailThreadingModesEnum, ChannelTypesEnum, parseAutoBcc, RAutoBcc} from './channelCommonTypes';

/*
 * Runtime type.
 */

export enum ChannelProvidersEnum {
  GMAIL = 'gmail',
  OFFICE365 = 'office365',
  SENDGRID = 'sendgrid',
  MANDRILL = 'mandrill',
  MANDRILL_3 = 'mandrill_3',
  CUSTOM = 'custom',
}

const RChatOfflineHours = t.readonly(
  t.type({
    enabled: t.boolean,
    timezone: t.string,
    hours: t.readonlyArray(
      t.type({
        days: t.readonlyArray(t.number),
        timerange: t.type({
          start_time: t.string,
          end_time: t.string,
        }),
      }),
    ),
  }),
);

export enum ChatEmailCaptureRequiredInfosEnum {
  NAME_AND_EMAIL = 'NAME_AND_EMAIL',
  EMAIL = 'EMAIL',
}

export enum ChatEmailCaptureModesEnum {
  NONE = 'NONE',
  OPTIONAL = 'OPTIONAL',
  REQUIRED = 'REQUIRED',
}

const RChatEmailCapture = t.readonly(
  t.intersection([
    t.type({
      enabled: t.boolean,
      required: t.boolean,
      prompt: t.string,
      timezone: t.string,
      requiredInfo: makeEnumRuntimeType(ChatEmailCaptureRequiredInfosEnum),
    }),
    t.partial({
      senderChannelId: t.number,
      mode: makeEnumRuntimeType(ChatEmailCaptureModesEnum),
    }),
  ]),
);

const RChatPushMessage = t.readonly(
  t.type({
    enabled: t.boolean,
    message: t.string,
  }),
);

export enum ChatPlacementLocationsEnum {
  BOTTOM_RIGHT = 'bottom_right',
  BOTTOM_LEFT = 'bottom_left',
}

const RChatPlacement = t.readonly(
  t.type({
    widgetPlacementLocation: makeEnumRuntimeType(ChatPlacementLocationsEnum),
    widgetSideSpacing: t.number,
    widgetBottomSpacing: t.number,
  }),
);

const RChatHideTeammateDetails = t.readonly(
  t.type({
    hideTeammateNames: t.boolean,
    hideTeammateAvatars: t.boolean,
  }),
);

const RChannelPreferencesBase = t.type({
  shouldAutoAssign: t.boolean,
  canUndoSend: t.boolean,
  undoSendPeriod: t.Integer,
  autoBcc: RAutoBcc,
  attachmentSizeLimit: t.Integer,
  canSend: t.boolean,
  recipientsCountLimit: t.Integer,
  canMassArchive: t.boolean,
  canReplyWithoutInboxAccess: t.boolean,
  isDemo: t.boolean,
  syncTags: t.boolean,
  importHistory: t.boolean,
});

export const RChannelPreferencesEmailBase = t.intersection([
  RChannelPreferencesBase,
  t.type({
    shouldEnableEmailTracking: t.boolean,
    addressAndSendAsRecords: t.readonlyArray(t.string),
  }),
  t.partial({
    provider: makeEnumRuntimeType(ChannelProvidersEnum),
    smtp: t.union([
      t.undefined,
      t.type({
        enabled: t.boolean,
        user: t.string,
        password: t.string,
        host: t.string,
        port: t.number,
        encryption: t.boolean,
      }),
    ]),
  }),
]);

export const RChannelPreferencesSmtp = t.intersection([
  RChannelPreferencesEmailBase,
  t.type({
    threadingMode: t.union([
      t.literal(ChannelEmailThreadingModesEnum.REGULAR),
      t.literal(ChannelEmailThreadingModesEnum.SUBJECT),
      t.literal(ChannelEmailThreadingModesEnum.DISABLED),
    ]),
  }),
]);

export const RChannelPreferencesFrontMail = t.intersection([
  RChannelPreferencesEmailBase,
  t.type({
    threadingMode: t.union([
      t.literal(ChannelEmailThreadingModesEnum.REGULAR),
      t.literal(ChannelEmailThreadingModesEnum.SUBJECT),
      t.literal(ChannelEmailThreadingModesEnum.DISABLED),
    ]),
  }),
]);

export const RChannelPreferencesGmail = t.intersection([
  RChannelPreferencesEmailBase,
  t.type({
    threadingMode: t.union([
      t.literal(ChannelEmailThreadingModesEnum.GMAIL),
      t.literal(ChannelEmailThreadingModesEnum.REGULAR),
      t.literal(ChannelEmailThreadingModesEnum.SUBJECT),
      t.literal(ChannelEmailThreadingModesEnum.DISABLED),
    ]),
  }),
]);

export const RChannelPreferencesOffice365 = t.intersection([
  RChannelPreferencesEmailBase,
  t.type({
    threadingMode: t.union([
      t.literal(ChannelEmailThreadingModesEnum.OFFICE365),
      t.literal(ChannelEmailThreadingModesEnum.OFFICE365_SUBJECT),
      t.literal(ChannelEmailThreadingModesEnum.SUBJECT_ONLY),
      t.literal(ChannelEmailThreadingModesEnum.DISABLED),
    ]),
  }),
]);

export const RChannelPreferencesCustom = t.intersection([
  RChannelPreferencesBase,
  t.type({
    webhookUrlOut: t.union([t.undefined, t.string]),
    contactType: makeEnumRuntimeType(ChannelContactTypesEnum),
    composeMode: makeEnumRuntimeType(ChannelComposeModesEnum),
    replyMode: t.union([
      t.literal(ChannelReplyModesEnum.SAME_CHANNEL),
      t.literal(ChannelReplyModesEnum.UNSUPPORTED),
    ]),
  }),
]);

export const RChannelPreferencesSmooch = t.intersection([
  RChannelPreferencesBase,
  t.type({
    importWhispers: t.boolean,
  }),
]);

export const RChannelPreferencesTwitter = t.intersection([
  RChannelPreferencesBase,
  t.type({
    includeRetweets: t.boolean,
  }),
]);

export const RChannelPreferencesFrontChat = t.intersection([
  RChannelPreferencesBase,
  t.type({
    widgetOfflineHours: RChatOfflineHours,
    widgetHeading: t.string,
    widgetSubHeading: t.string,
    enforceIdentityVerification: t.boolean,
    widgetEmailCapture: RChatEmailCapture,
    widgetPushMessage: RChatPushMessage,
    widgetSettingsPath: t.string,
    verifiedIdentitySecret: t.string,
    widgetHasUploadedLogo: t.boolean,
    widgetPlacement: RChatPlacement,
    hideTeammateDetails: RChatHideTeammateDetails,
    widgetColor: t.string,
    widgetSecondaryColor: t.string,
    widgetUseSecondaryColor: t.boolean,
    widgetCompanyName: t.string,
    shouldHideTeammateAvatars: t.boolean,
    shouldHideTeammateNames: t.boolean,
  }),
]);

const RChannelPreferencesDirty = t.readonly(
  t.intersection([
    RChannelPreferencesBase,
    t.partial({
      provider: makeEnumRuntimeType(ChannelProvidersEnum),
    }),
  ]),
);

/*
 * Type.
 */

export interface ChatOfflineHours extends t.TypeOf<typeof RChatOfflineHours> {}

export interface ChannelPreferencesCustomModel extends t.TypeOf<typeof RChannelPreferencesCustom> {}
export interface ChannelPreferencesFrontChatModel extends t.TypeOf<typeof RChannelPreferencesFrontChat> {}
export interface ChannelPreferencesSmoochModel extends t.TypeOf<typeof RChannelPreferencesSmooch> {}
export interface ChannelPreferencesTwitterModel extends t.TypeOf<typeof RChannelPreferencesTwitter> {}

export interface ChannelPreferencesSmtpModel extends t.TypeOf<typeof RChannelPreferencesSmtp> {}
export interface ChannelPreferencesFrontMailModel extends t.TypeOf<typeof RChannelPreferencesFrontMail> {}
export interface ChannelPreferencesGmailModel extends t.TypeOf<typeof RChannelPreferencesGmail> {}
export interface ChannelPreferencesOffice365Model extends t.TypeOf<typeof RChannelPreferencesOffice365> {}
export interface ChannelPreferencesEmailBaseModel extends t.TypeOf<typeof RChannelPreferencesEmailBase> {}

export type ChannelPreferencesEmailModel =
  | ChannelPreferencesSmtpModel
  | ChannelPreferencesFrontMailModel
  | ChannelPreferencesGmailModel
  | ChannelPreferencesOffice365Model;
interface ChannelPreferencesBase extends t.TypeOf<typeof RChannelPreferencesBase> {}

export type ChannelPreferencesModel =
  | ChannelPreferencesEmailModel
  | ChannelPreferencesFrontChatModel
  | ChannelPreferencesCustomModel
  | ChannelPreferencesSmoochModel
  | ChannelPreferencesTwitterModel
  | ChannelPreferencesBase;
export const RChannelPreferences = t.clean<ChannelPreferencesModel>(RChannelPreferencesDirty as any);

/*
 * Constants.
 */

const defaultChatHideTeammateDetails = Object.freeze({
  hideTeammateNames: false,
  hideTeammateAvatars: false,
});

/*
 * IO methods.
 */

export function parseChannelPreferencesBase(src: any): ChannelPreferencesBase {
  return {
    shouldAutoAssign: src.autoAssign,
    canUndoSend: src.undoSend,
    undoSendPeriod: src.undoSendPeriod,
    autoBcc: parseAutoBcc(src.autobcc),
    attachmentSizeLimit: src.attachmentSizeLimit,
    canSend: src.canSend,
    recipientsCountLimit: parseRecipientCountLimit(src.numRecipientsLimit),
    canMassArchive: src.displayMassArchiveBanner || false,
    canReplyWithoutInboxAccess: src.replyWithoutInboxAccess || false,
    isDemo: Boolean(src.demo),
    syncTags: src.syncTags,
    importHistory: src.importHistory,
  };
}

export function parseChannelPreferencesEmailBase(
  src: any,
  base: ChannelPreferencesBase,
): ChannelPreferencesEmailBaseModel {
  return {
    ...base,
    shouldEnableEmailTracking: Boolean(src.enableTracking),
    provider: src.provider,
    smtp: (src?.smtp?.enabled === true && src.smtp) || undefined,
    addressAndSendAsRecords: src.addressAndSendAsRecords || [],
  };
}

export function parseChannelPreferencesSmtp(
  src: any,
  base: ChannelPreferencesBase,
): ChannelPreferencesSmtpModel {
  return {
    ...parseChannelPreferencesEmailBase(src, base),
    threadingMode: src.threadingMode,
  };
}

export function parseChannelPreferencesOffice365(
  src: any,
  base: ChannelPreferencesBase,
): ChannelPreferencesOffice365Model {
  return {
    ...parseChannelPreferencesEmailBase(src, base),
    threadingMode: src.threadingMode,
  };
}

export function parseChannelPreferencesGmail(
  src: any,
  base: ChannelPreferencesBase,
): ChannelPreferencesGmailModel {
  return {
    ...parseChannelPreferencesEmailBase(src, base),
    threadingMode: src.threadingMode,
  };
}

export function parseChannelPreferencesFrontMail(
  src: any,
  base: ChannelPreferencesBase,
): ChannelPreferencesFrontMailModel {
  return {
    ...parseChannelPreferencesEmailBase(src, base),
    threadingMode: src.threadingMode,
  };
}

export function parseChannelPreferencesFrontChat(
  src: any,
  base: ChannelPreferencesBase,
): ChannelPreferencesFrontChatModel {
  return {
    ...base,
    enforceIdentityVerification: src.enforceIdentityVerification ?? true,
    widgetOfflineHours: parseOfflineHours(src),
    widgetSubHeading: src.widgetSubHeading,
    widgetHeading: src.widgetHeading,
    widgetEmailCapture: src.widgetEmailCapture,
    widgetPushMessage: src.widgetPushMessage,
    widgetSettingsPath: src.widgetSettingsPath,
    verifiedIdentitySecret: src.verifiedIdentitySecret,
    widgetHasUploadedLogo: src.widgetHasUploadedLogo,
    widgetPlacement: src.widgetPlacement,
    hideTeammateDetails: src.hideTeammateDetails ?? defaultChatHideTeammateDetails,
    widgetColor: src.widgetColor,
    widgetSecondaryColor: src.widgetSecondaryColor,
    widgetUseSecondaryColor: src.widgetUseSecondaryColor,
    widgetCompanyName: src.widgetCompanyName,
    shouldHideTeammateNames: src.hideTeammateDetails?.hideTeammateNames ?? false,
    shouldHideTeammateAvatars: src.hideTeammateDetails?.hideTeammateAvatars ?? false,
  };
}

export function parseChannelPreferencesSmooch(
  src: any,
  base: ChannelPreferencesBase,
): ChannelPreferencesSmoochModel {
  return {
    ...base,
    importWhispers: src.importWhispers ?? false,
  };
}

export function parseChannelPreferencesTwitter(
  src: any,
  base: ChannelPreferencesBase,
): ChannelPreferencesTwitterModel {
  return {
    ...base,
    includeRetweets: src.includeRetweets ?? false,
  };
}

export function parseChannelPreferencesCustom(
  src: any,
  base: ChannelPreferencesBase,
): ChannelPreferencesCustomModel {
  return {
    ...parseChannelPreferencesEmailBase(src, base),
    contactType: src.contactType,
    composeMode: src.composeMode,
    replyMode: src.replyMode,
    webhookUrlOut: src.account.webhookUrlOut,
  };
}

export function serializeChannelPreferences(src: ChannelPreferencesModel, type: ChannelTypesEnum): any {
  const baseProps = {
    autoAssign: src.shouldAutoAssign,
    undoSend: src.canUndoSend,
    undoSendPeriod: src.undoSendPeriod,
    replyWithoutInboxAccess: src.canReplyWithoutInboxAccess,
    attachmentSizeLimit: src.attachmentSizeLimit,
    canSend: src.canSend,
    importHistory: src.importHistory,
    syncTags: src.syncTags,
  };

  if (type === ChannelTypesEnum.FRONT_CHAT) {
    const chatPreferences = src as ChannelPreferencesFrontChatModel;
    return {
      ...baseProps,
      widgetCompanyName: chatPreferences.widgetCompanyName,
      widgetPlacement: chatPreferences.widgetPlacement,
      widgetColor: chatPreferences.widgetColor,
      widgetSecondaryColor: chatPreferences.widgetSecondaryColor,
      widgetUseSecondaryColor: chatPreferences.widgetUseSecondaryColor,
      widgetHeading: chatPreferences.widgetHeading,
      widgetSubHeading: chatPreferences.widgetSubHeading,
      widgetEmailCapture: chatPreferences.widgetEmailCapture,
      widgetPushMessage: chatPreferences.widgetPushMessage,
      widgetOfflineHoursTimezone: chatPreferences.widgetOfflineHours.timezone,
      widgetOfflineHoursEnabled: chatPreferences.widgetOfflineHours.enabled,
      widgetOfflineHours: chatPreferences.widgetOfflineHours.hours,
      enforceIdentityVerification: chatPreferences.enforceIdentityVerification,
      widgetHasUploadedLogo: chatPreferences.widgetHasUploadedLogo,
      hideTeammateDetails: {
        hideTeammateAvatars: chatPreferences.shouldHideTeammateAvatars,
        hideTeammateNames: chatPreferences.shouldHideTeammateNames,
      },
    };
  }

  if (
    type === ChannelTypesEnum.OFFICE365 ||
    type === ChannelTypesEnum.SMTP ||
    type === ChannelTypesEnum.GMAIL ||
    type === ChannelTypesEnum.FRONT_MAIL
  ) {
    const baseMailPreferences = src as ChannelPreferencesEmailBaseModel;
    const serializedBaseMailPreferences = {
      enableTracking: baseMailPreferences.shouldEnableEmailTracking,
      addressAndSendAsRecords: baseMailPreferences.addressAndSendAsRecords,
      smtp: baseMailPreferences.smtp,
      provider: baseMailPreferences.provider,
    };

    if (type === ChannelTypesEnum.OFFICE365) {
      const officePreferences = src as ChannelPreferencesOffice365Model;
      return {
        ...baseProps,
        ...serializedBaseMailPreferences,
        threadingMode: officePreferences.threadingMode,
      };
    }
    if (type === ChannelTypesEnum.GMAIL) {
      const gmailPreferences = src as ChannelPreferencesGmailModel;
      return {
        ...baseProps,
        ...serializedBaseMailPreferences,
        threadingMode: gmailPreferences.threadingMode,
      };
    }
    if (type === ChannelTypesEnum.SMTP) {
      const smtpPreferences = src as ChannelPreferencesSmtpModel;
      return {
        ...baseProps,
        ...serializedBaseMailPreferences,
        threadingMode: smtpPreferences.threadingMode,
      };
    }
    if (type === ChannelTypesEnum.FRONT_MAIL) {
      const frontMailPreferences = src as ChannelPreferencesFrontMailModel;
      return {
        ...baseProps,
        ...serializedBaseMailPreferences,
        threadingMode: frontMailPreferences.threadingMode,
      };
    }
  }

  if (type === ChannelTypesEnum.CUSTOM) {
    const customPreferences = src as ChannelPreferencesCustomModel;
    return {
      ...baseProps,
      account: {webhookUrlOut: customPreferences.webhookUrlOut},
      contactType: customPreferences.contactType,
      composeMode: customPreferences.composeMode,
      replyMode: customPreferences.replyMode,
    };
  }

  if (type === ChannelTypesEnum.SMOOCH) {
    const customPreferences = src as ChannelPreferencesSmoochModel;
    return {
      ...baseProps,
      importWhispers: customPreferences.importWhispers,
    };
  }

  if (type === ChannelTypesEnum.TWITTER) {
    const customPreferences = src as ChannelPreferencesTwitterModel;
    return {
      ...baseProps,
      includeRetweets: customPreferences.includeRetweets,
    };
  }

  return {
    ...baseProps,
  };
}

function parseOfflineHours(src: any): ChatOfflineHours {
  return {
    enabled: Boolean(src.widgetOfflineHoursEnabled) ?? false,
    hours: src.widgetOfflineHours ?? [],
    timezone: src.widgetOfflineHoursTimezone ?? findTimeZone(),
  };
}

function parseRecipientCountLimit(numRecipientsLimit: any) {
  return Number.isInteger(numRecipientsLimit) ? numRecipientsLimit : 100;
}
